import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getHand, updateHand, deleteHand } from '../api/hands';
import { checkFavorite, addFavorite, removeFavorite } from '../api/favorites';
import { API_ORIGIN } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useVotes, useCreateVote } from '../hooks/useVotes';
import VotePanel from '../components/voting/VotePanel';
import Spinner from '../components/common/Spinner';

export default function AnalysisPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleInput, setTitleInput] = useState('');

  const { data: hand, isLoading, error } = useQuery({
    queryKey: ['hand', id],
    queryFn: () => getHand(id!),
    enabled: !!id,
  });

  const deletePost = useMutation({
    mutationFn: () => deleteHand(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hands'] });
      navigate('/');
    },
  });

  const updateTitle = useMutation({
    mutationFn: (title: string) => updateHand(id!, { title }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['hand', id], updated);
      queryClient.invalidateQueries({ queryKey: ['hands'] });
      setEditingTitle(false);
    },
  });

  const { data: votes, isLoading: votesLoading } = useVotes(id);
  const createVote = useCreateVote(id!);

  const { data: favData } = useQuery({
    queryKey: ['favorite', id],
    queryFn: () => checkFavorite(id!),
    enabled: !!id && !!user,
  });
  const isFavorited = favData?.is_favorited ?? false;

  const toggleFavorite = useMutation({
    mutationFn: () => isFavorited ? removeFavorite(id!) : addFavorite(id!),
    onSuccess: () => {
      const delta = isFavorited ? -1 : 1;
      queryClient.setQueryData(['favorite', id], { is_favorited: !isFavorited });
      queryClient.setQueryData(['hand', id], (old: any) =>
        old ? { ...old, favorite_count: (old.favorite_count ?? 0) + delta } : old
      );
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !hand) {
    return (
      <div className="text-center py-20 text-red-600">
        局面が見つかりません
      </div>
    );
  }

  const imageUrl = hand.source_image_url
    ? hand.source_image_url.startsWith('/')
      ? `${API_ORIGIN}${hand.source_image_url}`
      : hand.source_image_url
    : null;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 space-y-4">
      {/* Title */}
      <div className="flex items-center justify-between gap-3">
        {editingTitle ? (
          <form
            className="flex items-center gap-2 flex-1"
            onSubmit={(e) => {
              e.preventDefault();
              const trimmed = titleInput.trim();
              if (trimmed) updateTitle.mutate(trimmed);
            }}
          >
            <input
              autoFocus
              className="flex-1 text-xl font-bold border-b-2 border-green-500 outline-none bg-transparent"
              value={titleInput}
              onChange={(e) => setTitleInput(e.target.value)}
              maxLength={100}
              onKeyDown={(e) => { if (e.key === 'Escape') setEditingTitle(false); }}
            />
            <button
              type="submit"
              disabled={updateTitle.isPending}
              className="text-xs text-green-600 font-medium hover:underline shrink-0 disabled:opacity-50"
            >
              {updateTitle.isPending ? '保存中...' : '保存'}
            </button>
            <button
              type="button"
              onClick={() => setEditingTitle(false)}
              className="text-xs text-gray-400 hover:text-gray-600 shrink-0"
            >
              キャンセル
            </button>
            {updateTitle.isError && (
              <span className="text-xs text-red-500">
                失敗: {updateTitle.error instanceof Error ? updateTitle.error.message : String(updateTitle.error)}
              </span>
            )}
          </form>
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-xl font-bold text-gray-900 truncate">
              {hand.title ?? '何切る？'}
            </h1>
            {user && hand.user_id === user.id && (
              <button
                onClick={() => { setTitleInput(hand.title ?? ''); setEditingTitle(true); }}
                className="text-gray-400 hover:text-gray-600 shrink-0"
                title="タイトルを編集"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </button>
            )}
          </div>
        )}
        {!editingTitle && (
          <div className="flex items-center gap-3 shrink-0">
            {hand.user && (
              <Link to={`/users/${hand.user.x_username}`} className="flex items-center gap-2 text-sm text-gray-500 hover:opacity-75">
                {hand.user.x_avatar_url && (
                  <img src={hand.user.x_avatar_url} alt="" className="w-6 h-6 rounded-full" />
                )}
                <span>@{hand.user.x_username}</span>
              </Link>
            )}
            {user && (
              <button
                onClick={() => toggleFavorite.mutate()}
                disabled={toggleFavorite.isPending}
                className={`flex items-center gap-1 transition-colors disabled:opacity-50 ${isFavorited ? 'text-yellow-400' : 'text-gray-400 hover:text-yellow-400'}`}
                title={isFavorited ? 'お気に入りを解除' : 'お気に入りに追加'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} fill={isFavorited ? 'currentColor' : 'none'}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                </svg>
                {hand.favorite_count > 0 && (
                  <span className="text-xs font-medium">{hand.favorite_count}</span>
                )}
              </button>
            )}
            {user && hand.user_id === user.id && (
              <button
                onClick={() => {
                  if (window.confirm('この局面を削除しますか？')) deletePost.mutate();
                }}
                disabled={deletePost.isPending}
                className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                title="削除"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Uploaded image */}
      {imageUrl && (
        <img
          src={imageUrl}
          alt="麻雀の局面"
          className="w-full rounded border object-contain"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      )}

      {/* Vote panel (includes comment thread) */}
      <VotePanel
        handId={id!}
        title={hand.title}
        situationType={hand.situation_type}
        aggregate={votes ?? null}
        isLoading={votesLoading}
        onVote={async (payload) => {
          await createVote.mutateAsync(payload);
        }}
      />
    </div>
  );
}
